import React, { useState, useEffect, createContext, useContext, useCallback } from 'react';
import { Bell, X, DollarSign, FileText, Users, Star, MessageSquare, CheckCircle, AlertCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Card } from './ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { ScrollArea } from './ui/scroll-area';
import { useConfetti } from '../hooks/useConfetti';
import { useAuth } from '../context/AuthContext';
import useSocket from '../hooks/useSocket';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

// Notification Context
const NotificationContext = createContext(null);

export function useNotifications() {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within NotificationProvider');
  }
  return context;
}

// Icon mapping
const getIconForType = (type) => {
  switch (type) {
    case 'deal_won': return DollarSign;
    case 'deal_status': return FileText;
    case 'quote_received': return FileText;
    case 'new_inquiry': return Users;
    case 'review': return Star;
    case 'message': return MessageSquare;
    case 'profile_view': return CheckCircle;
    default: return Bell;
  }
};

const getColorForType = (type) => {
  switch (type) {
    case 'deal_won': return 'text-green-500 bg-green-500/10';
    case 'deal_status': return 'text-blue-500 bg-blue-500/10';
    case 'quote_received': return 'text-blue-500 bg-blue-500/10';
    case 'new_inquiry': return 'text-purple-500 bg-purple-500/10';
    case 'review': return 'text-amber-500 bg-amber-500/10';
    case 'message': return 'text-primary bg-primary/10';
    default: return 'text-gray-500 bg-gray-500/10';
  }
};

// Format relative time
function formatRelativeTime(dateString) {
  if (!dateString) return '';
  const date = new Date(dateString);
  const now = new Date();
  const diff = now - date;
  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes}m ago`;
  if (hours < 24) return `${hours}h ago`;
  return `${days}d ago`;
}

// Notification Provider
export function NotificationProvider({ children }) {
  const { user } = useAuth();
  const token = localStorage.getItem('token');
  const queryClient = useQueryClient();
  const [showToast, setShowToast] = useState(null);
  const { celebrateDealWon, celebrateQuoteAccepted } = useConfetti();

  // --- Real-time WebSocket ---
  const handleWebSocketMessage = useCallback((message) => {
    // Optimistically update query cache
    queryClient.setQueryData(['notifications'], (old) => {
      const newNotif = { ...message, is_realtime: true }; // Marker for animation if needed
      return [newNotif, ...(old || [])];
    });

    // Show Toast
    setShowToast({
      ...message,
      icon: getIconForType(message.type),
      color: getColorForType(message.type)
    });

    // Validations & Celebrations
    if (message.type === 'deal_won') {
      celebrateDealWon();
    }

    // Auto-hide toast
    setTimeout(() => setShowToast(null), 5000);
  }, [queryClient, celebrateDealWon]);

  // Only connect if we have a token (user is logged in)
  const wsUrl = token
    ? `${process.env.REACT_APP_BACKEND_URL.replace('http', 'ws')}/api/notifications/ws?token=${token}`
    : null;

  useSocket(wsUrl, {
    onMessage: handleWebSocketMessage,
    shouldReconnect: true
  });

  // --- API Data Fetching ---
  const { data: notifications = [] } = useQuery({
    queryKey: ['notifications'],
    queryFn: async () => {
      if (!token) return [];
      const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/notifications`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return res.data;
    },
    enabled: !!token,
    refetchInterval: 60000 // Poll every minute as backup
  });

  // Mutations
  const markReadMutation = useMutation({
    mutationFn: async (id) => {
      await axios.patch(`${process.env.REACT_APP_BACKEND_URL}/api/notifications/${id}/read`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
    },
    // Optimistic Update
    onMutate: async (id) => {
      // Cancel any outgoing refetches (so they don't overwrite our optimistic update)
      await queryClient.cancelQueries(['notifications']);

      // Snapshot the previous value
      const previousNotifications = queryClient.getQueryData(['notifications']);

      // Optimistically update to the new value
      queryClient.setQueryData(['notifications'], (old) => {
        return old?.map(n =>
          (n._id === id || n.id === id) ? { ...n, read: true } : n
        ) || [];
      });

      // Return a context object with the snapshotted value
      return { previousNotifications };
    },
    // If the mutation fails, use the context returned from onMutate to roll back
    onError: (err, id, context) => {
      queryClient.setQueryData(['notifications'], context.previousNotifications);
      console.error("Failed to mark notification as read:", err);
    },
    // Always refetch after error or success:
    onSettled: () => {
      queryClient.invalidateQueries(['notifications']);
    }
  });

  const markAllReadMutation = useMutation({
    mutationFn: async () => {
      await axios.patch(`${process.env.REACT_APP_BACKEND_URL}/api/notifications/read-all`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['notifications']);
    }
  });

  const deleteMutation = useMutation({
    mutationFn: async (id) => {
      await axios.delete(`${process.env.REACT_APP_BACKEND_URL}/api/notifications/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
    },
    // Optimistic Update
    onMutate: async (id) => {
      // Cancel refetches
      await queryClient.cancelQueries(['notifications']);

      // Snapshot previous value
      const previousNotifications = queryClient.getQueryData(['notifications']);

      // Optimistically update
      queryClient.setQueryData(['notifications'], (old) => {
        return old?.filter(n => (n._id !== id && n.id !== id)) || [];
      });

      // Return context
      return { previousNotifications };
    },
    // Rollback on error
    onError: (err, id, context) => {
      queryClient.setQueryData(['notifications'], context.previousNotifications);
      console.error("Failed to delete notification:", err);
    },
    // Refetch on settled
    onSettled: () => {
      queryClient.invalidateQueries(['notifications']);
    }
  });

  const unreadCount = notifications.filter(n => !n.read).length;

  const markAsRead = (id) => markReadMutation.mutate(id);
  const markAllAsRead = () => markAllReadMutation.mutate();
  const clearNotification = (id) => deleteMutation.mutate(id);

  // Expose mock addNotification for demo purposes if needed, but primarily use real data
  const addNotification = (n) => {
    // Local optimisitc update
    handleWebSocketMessage(n);
  };

  return (
    <NotificationContext.Provider value={{
      notifications,
      unreadCount,
      addNotification,
      markAsRead,
      markAllAsRead,
      clearNotification
    }}>
      {children}

      {/* Toast Notification */}
      {showToast && (
        <div className="fixed bottom-6 left-6 z-50 animate-in slide-in-from-left-4">
          <Card className="p-4 shadow-lg border-l-4 border-l-primary max-w-sm">
            <div className="flex items-start gap-3">
              <div className={`p-2 rounded-full ${showToast.color}`}>
                <showToast.icon className="h-4 w-4" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-sm">{showToast.title}</p>
                <p className="text-xs text-muted-foreground truncate">{showToast.message}</p>
              </div>
              <button onClick={() => setShowToast(null)} className="text-muted-foreground hover:text-foreground">
                <X className="h-4 w-4" />
              </button>
            </div>
          </Card>
        </div>
      )}
    </NotificationContext.Provider>
  );
}

// Notification Bell Component
export function NotificationBell() {
  const { notifications, unreadCount, markAsRead, markAllAsRead, clearNotification } = useNotifications();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const handleNotificationClick = (notification) => {
    markAsRead(notification._id || notification.id);
    setOpen(false); // Close dropdown

    if (notification.link) {
      navigate(notification.link);
    }
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="relative" data-testid="notification-bell">
          <Bell className="h-5 w-5" />
          {unreadCount > 0 && (
            <Badge
              className="absolute -top-1 -right-1 h-5 w-5 p-0 flex items-center justify-center text-xs"
              variant="destructive"
            >
              {unreadCount > 9 ? '9+' : unreadCount}
            </Badge>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-[calc(100vw-2rem)] md:w-80 p-0">
        {/* Header */}
        <div className="flex items-center justify-between p-3 border-b">
          <h3 className="font-semibold">Notifications</h3>
          {unreadCount > 0 && (
            <Button variant="ghost" size="sm" className="text-xs h-7" onClick={markAllAsRead}>
              Mark all read
            </Button>
          )}
        </div>

        {/* Notifications List */}
        <ScrollArea className="h-80">
          {notifications.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground">
              <Bell className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p className="text-sm">No notifications yet</p>
            </div>
          ) : (
            <div className="divide-y">
              {notifications.map((notification) => {
                const Icon = getIconForType(notification.type);
                const color = getColorForType(notification.type);

                return (
                  <div
                    key={notification._id || notification.id}
                    className={`group p-3 hover:bg-muted/50 cursor-pointer transition-colors border-b last:border-0 ${!notification.read ? 'bg-primary/5' : ''}`}
                    onClick={() => handleNotificationClick(notification)}
                  >
                    <div className="flex gap-3 items-start">
                      <div className={`p-2 rounded-full ${color} shrink-0 mt-0.5`}>
                        <Icon className="h-4 w-4" />
                      </div>
                      <div className="flex-1 min-w-0 space-y-1">
                        <div className="flex items-start justify-between gap-2">
                          <p className={`text-sm leading-none ${!notification.read ? 'font-semibold' : 'font-medium'}`}>
                            {notification.title}
                          </p>
                          <button
                            onClick={(e) => { e.stopPropagation(); clearNotification(notification._id || notification.id); }}
                            className="text-muted-foreground hover:text-foreground shrink-0 opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </div>
                        <p className="text-xs text-muted-foreground line-clamp-2 leading-snug">{notification.message}</p>
                        <p className="text-[10px] text-muted-foreground">{formatRelativeTime(notification.created_at || notification.time)}</p>
                      </div>
                      {!notification.read && (
                        <div className="w-2 h-2 rounded-full bg-primary shrink-0 mt-1.5" />
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </ScrollArea>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default NotificationBell;
